import AddSharpIcon from '@mui/icons-material/AddSharp';
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { IconButton,Typography } from "@mui/material";
import Modal from "react-bootstrap/Modal";
import Tooltip from "@mui/material/Tooltip";
import { DataGrid, GridColDef, GridRowId } from "@mui/x-data-grid";
import React, { useEffect, useRef, useState } from "react";
import { usePersistentState, markNavigationPreserve, consumeNavigationPreserve, clearNamespace } from "../../hooks/persistence";
import { useHistory, useLocation } from "react-router-dom";
import { Service } from "../../Service/ApiService";
import { CommonService } from "../../Service/CommonService";
import "../Dynamic/UserList.scss";
import DeleteCompany from "../popups/DeleteCompany";
import SuspendCompany from '../popups/SuspendCompany';
import ActiveCompany from '../popups/ActiveCompany';
import CheckIcon from '@mui/icons-material/Check';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import EditIcon from '../../assets/images/icons/EditIcon.svg';
import DeleteIcon from "../../assets/images/icons/Trash_Black.svg";
import CloseIcon from "@mui/icons-material/Close";
import ScannedWarning from "../../assets/images/icons/ScannedWarning.svg";
import Spinner from '../Common/Spinner';
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import DataGridLoader from '../Common/DataGridLoader';

const CompanyList: React.FC = () => {
  const [selectionModel, setSelectionModel] = React.useState<GridRowId[]>([]);
  const [companiesList, setCompaniesList] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const history = useHistory();
  const location = useLocation();

  const getActiveTab = () => {
    if (isSuspendedCompanyClicked.current) return "suspended";
    if (isAllCompanyClicked.current) return "all";
    return "active";
  };

  const pushWithActiveTab = (path: string) => {
    const activeTab = getActiveTab();
    markNavigationPreserve("company-list");
    history.push(path, { activeTab, fromInternal: true });
  };
  const [totalCount, setTotalCount] = useState(0);
  const controllerRef = useRef<AbortController | null>(null);

  const initialFilter = {
    companyName: null,
    contactPerson: null,
    contactNumber: null,
    email: null,
    isSuspended: null
  };

  const _filterInitRef = useRef(false);
  if (!_filterInitRef.current) {
    _filterInitRef.current = true;
    if (!consumeNavigationPreserve("company-list")) {
      clearNamespace("company-list");
    }
  }

  const [search, setSearch] = usePersistentState<string>("company-list", "search", "");
  const [page, setPage] = usePersistentState<number>("company-list", "page", 0);
  const [pageSize, setPageSize] = usePersistentState<number>("company-list", "pageSize", 50);
  const [sortField, setSortField] = usePersistentState<string>("company-list", "sortField", "companyName");
  const [sortDirection, setSortDirection] = usePersistentState<string>("company-list", "sortDirection", "asc");
  const [filter, setFilter] = usePersistentState<any>("company-list", "filter", initialFilter);
  const isAllCompanyClicked = useRef(false);
  const isActiveCompanyClicked = useRef(false);
  const isSuspendedCompanyClicked = useRef(false);
  const [allCompanyCount, setAllCompanyCount] = useState(0);
  const [activeCompanyCount, setActiveCompanyCount] = useState(0);
  const [suspendedCompanyCount, setSuspendedCompanyCount] = useState(0);
  const [deleteCompany, setDeleteCompany] = useState(false);
  const [isCompanyDeletable, setIsCompanyDeletable] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);

  const handleClose = () => {
    setDeleteCompany(false); 
  };

  // Define the localeText object based on the isLoading flag
  const localeText = {
    noRowsLabel: isLoading ? "Fetching records ..." : "No Record Found",
  };

  const showActiveCompanies = async (page, size, filter: any, sortField: any, sortDirection: any, isSearching:any) => {

    //set active tab
    isActiveCompanyClicked.current = true;
    isAllCompanyClicked.current = false;
    isSuspendedCompanyClicked.current = false;

    const controller = new AbortController();
    const signal = controller.signal;
    try{
      setIsLoading(true);
      setCompaniesList([]);
      // Cancel the previous request if any
      if (controllerRef.current) {
        controllerRef.current.abort();
      }

      // Store the current controller to cancel the request if needed
      controllerRef.current = controller;

      const response: any = await Service.fetchAllActiveCompanies(page , size, filter, sortField, sortDirection, signal);
      setCompaniesList(response.data.content);
      setTotalCount(response.data.totalElements);
       if (!isSearching) {
        setActiveCompanyCount(response.data.totalElements);
      }
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setIsLoading(false);
    }
  }

  const showSuspendedCompanies = async (page, size, filter: any, sortField: any, sortDirection: any, isSearching:any) => {

    //set active tab
    isSuspendedCompanyClicked.current = true;
    isActiveCompanyClicked.current = false;
    isAllCompanyClicked.current = false;
    
    const controller = new AbortController();
    const signal = controller.signal;
    try{
      setIsLoading(true);
      setCompaniesList([]);
      // Cancel the previous request if any
      if (controllerRef.current) {
        controllerRef.current.abort();
      }

      // Store the current controller to cancel the request if needed
      controllerRef.current = controller;

      const response: any = await Service.fetchAllSuspendedCompanies(page , size, filter, sortField, sortDirection, signal);
      setCompaniesList(response.data.content);
      setTotalCount(response.data.totalElements);
      if (!isSearching) {
        setSuspendedCompanyCount(response.data.totalElements);
      }
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setIsLoading(false);
    }
  }
  
  const fetchAllCompaniesCount = async () => {
    try {
      const response = await Service.fetchAllCompaniesCount();
      setAllCompanyCount(response.data);
    } catch (error) {
      console.log("Error occurred while Api calling " + error);
    }
  }

  const fetchAllActiveCompaniesCount = async () => {
    try {
      const response = await Service.fetchAllActiveCompaniesCount();
      setActiveCompanyCount(response.data);
    } catch (error) {
      console.log("Error occurred while Api calling " + error);
    }
  }

  const fetchAllSuspendedCompaniesCount = async () => {
    try {
      const response = await Service.fetchAllSuspendedCompaniesCount();
      setSuspendedCompanyCount(response.data);
    } catch (error) {
      console.log("Error occurred while Api calling " + error);
    }
  }

  const showAllCompanies = async (page, size, filter, sortField, sortDirection, isSearching:any) => {
    //set active tab
    isAllCompanyClicked.current = true;
    isSuspendedCompanyClicked.current = false;
    isActiveCompanyClicked.current = false;

    const controller = new AbortController();
    const signal = controller.signal;
    try {
      setIsLoading(true);
      setCompaniesList([]);
      // Cancel the previous request if any
      if (controllerRef.current) {
        controllerRef.current.abort();
      }

      // Store the current controller to cancel the request if needed
      controllerRef.current = controller;

      const response: any = await Service.fetchCompaniesWithPagination(page, size, filter, sortField, sortDirection, signal);
      setCompaniesList(response.data.content);
      setTotalCount(response.data.totalElements);
       if (!isSearching) {
        setAllCompanyCount(response.data.totalElements);
      }
      isAllCompanyClicked.current = true;
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const restoredTab = (location.state as any)?.activeTab;
    if (restoredTab === "suspended") {
      showSuspendedCompanies(page, pageSize, filter, sortField, sortDirection, false);
      fetchAllCompaniesCount();
      fetchAllActiveCompaniesCount();
    } else if (restoredTab === "all") {
      showAllCompanies(page, pageSize, filter, sortField, sortDirection, false);
      fetchAllSuspendedCompaniesCount();
      fetchAllActiveCompaniesCount();
    } else {
      showActiveCompanies(page, pageSize, filter, sortField, sortDirection, false);
      fetchAllCompaniesCount();
      fetchAllSuspendedCompaniesCount();
    }
  }, []);

  //add tooltip
  const CellWithTooltip = ({ value }: { value: string | number }) => {
    return (
      <Tooltip arrow title={value?.toString()}>
        <span>{value?.toString()}</span>
      </Tooltip>
    );
  };

  //redirect to the add company page
  const handleAddCompany = () => {
    pushWithActiveTab("/master/add-company");
  };

  const handleRowClick = (params) => {
    const linkPrefix = CommonService.isAdmin() ? "/master" : "/user";
    pushWithActiveTab(`/master/company-dashboard/${params.row.id ?? ""}`);
  };

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>, field: keyof any) => {
    const value = event.target.value;
  
    // Prepare a temporary filter object with the updated field value
    const tempFilter = {
      ...filter, // Spread existing filter state
      [field]: value, // Update the specific field
    };
  
    // Update the filter state
    setFilter(tempFilter);
  
    // Load the service requests with updated filters
    if (isActiveCompanyClicked.current) {
      showActiveCompanies(page, pageSize, tempFilter, sortField, sortDirection, true);
    } else if (isSuspendedCompanyClicked.current) {
      showSuspendedCompanies(page, pageSize, tempFilter, sortField, sortDirection, true);
    } else {
      showAllCompanies(page, pageSize, tempFilter, sortField, sortDirection, true);
    }
  };

  const handleSortChange = (sortModel) => {
    if (sortModel.length > 0) {
      const newSortField = sortModel[0].field;
      const newSortDirection = sortModel[0].sort; // "asc" or "desc" directly from DataGrid

      setSortField(newSortField);
      setSortDirection(newSortDirection);

      //fetch all the companies based of active tab
      if (isActiveCompanyClicked) {
        showActiveCompanies(page, pageSize, filter, newSortField, newSortDirection, false);
      } else if (isSuspendedCompanyClicked) {
        showSuspendedCompanies(page, pageSize, filter, newSortField, newSortDirection, false);
      } else {
        showAllCompanies(page, pageSize, filter, newSortField, newSortDirection, false);
      }
    }
  };

  const onPaginationModelChange = (newPaginationModel) => {
    setPage(newPaginationModel.page);
    setPageSize(newPaginationModel.pageSize);

    //fetch the companies based of active tab
    if (isActiveCompanyClicked) {
      showActiveCompanies(newPaginationModel.page, newPaginationModel.pageSize, filter, sortField, sortDirection, false);  
    } else if (isSuspendedCompanyClicked) {
      showSuspendedCompanies(newPaginationModel.page, newPaginationModel.pageSize, filter, sortField, sortDirection, false);
    } else {
      showAllCompanies(newPaginationModel.page, newPaginationModel.pageSize, filter, sortField, sortDirection, false);
    }
  };

  const handleEditCompany = (id:any) => {
    // Navigate to the edit company page
    pushWithActiveTab("/master/edit-company/"+ id);
  };

  /**
   * Checks whether a account owner can be deleted and opens the delete confirmation flow.
   * This function:
   * 1. Calls the API to verify deletion eligibility
   * 2. Stores the selected account owner ID
   * 3. Sets deletion flags based on the API response
   */
  const handleDeleteCompany = async (companyId: string) => {
    try {
      const response = await Service.checkCompanyEligibility(companyId);
      const companyNotDeletable = response?.data;

      setSelectedCompanyId(companyId);
      setIsCompanyDeletable(companyNotDeletable === true);
      setDeleteCompany(true); 
    } catch (error) {
      console.error("This account owner cannot be deleted because it has active dependencies.");
    }
  };

  /**
   * Confirms and executes the account owner  deletion.
   * This function:
   * 1. Validates that a account owner  is selected
   * 2. Calls the delete API
   * 3. Refreshes the table and resets UI states
   */
  const confirmDeleteCompany = async () => {
    if (!selectedCompanyId) return; 

    try {
      setIsLoading(true);
      await Service.removeCompanyFromActiveList(selectedCompanyId); 
      reloadTable();
      setDeleteCompany(false);
    } catch (error) {
      console.error("Failed to delete account owner :", error);
    } finally {
      setIsLoading(false);
      setSelectedCompanyId(null);
    }
  };

  const exportCompanies = async (page: any, size: any, filter: any, sortField: any, sortDirection: any) => {
    const controller = new AbortController();
    const signal = controller.signal;

    try {
      setIsLoading(true);

      // Cancel the previous request if any
      if (controllerRef.current) {
        controllerRef.current.abort();
      }
      let tempFilter = { ...filter };

      if (isActiveCompanyClicked.current) {
        tempFilter.isSuspended = false;
      } else if (isSuspendedCompanyClicked.current) {
        tempFilter.isSuspended = true;
      } else {
        tempFilter.isSuspended = null;
      }

      setFilter(tempFilter);

      // Store the current controller to cancel the request if needed
      controllerRef.current = controller;
      Service.exportCompanies(tempFilter, sortField, sortDirection, signal);
      CommonService.Toast.fire({
        title: "Your export is in progress. We'll notify you when it's ready!",
        icon: "success",
      });
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setIsLoading(false);
    }
  };

  const columns: GridColDef[] = [
    {
      field: "companyName",
      headerName: "Account Owner Name",
      flex: 1,
      minWidth: 200,
      sortable: true,
      valueGetter: (value: any, row: any) => `${row.companyName ?? ""}`,
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: (params) => (
        <div>
          <Typography
            variant="body2"
            sx={{ color: "#6F6F6F", fontSize: "14px" }}
          >
            {params.colDef.headerName}
          </Typography>
          <div className="position-relative">
            <input
              type="text"
              className="form-control border mt-1"
              style={{ textIndent: "24px", boxShadow: "none" }}
              placeholder="Search"
              // value = {filter.companyName}
              defaultValue={filter.companyName}
              onChange={(event) => handleFilterChange(event, "companyName")}
              onClick={(event) => event.stopPropagation()}
            />
            <SearchIcon
              sx={{
                color: "#6F6F6F",
                position: "absolute",
                top: "50%",
                left: "8px",
                transform: "translateY(-50%)",
              }}
            />
          </div>
        </div>
      ),
      renderCell: (params: any) => (
        <CellWithTooltip value={params.row.companyName} />
      ),
    },
    {
      field: "totalInventoryCount",
      headerName: "Total Inventory Count",
      flex: 1,
      minWidth: 200,
      sortable: true,
      valueGetter: (value: any, row: any) => `${row.totalInventoryCount ?? ""}`,
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: (params) => (
        <div>
          <Typography
            variant="body2"
            sx={{ color: "#6F6F6F", fontSize: "14px" }}
          >
            {params.colDef.headerName}
          </Typography>
          <div className="position-relative">
            <input
              type="text"
              className="form-control border mt-1"
              style={{ textIndent: "24px", boxShadow: "none" }}
              placeholder="Search"
              defaultValue={filter.totalInventoryCount}
              onChange={(event) => handleFilterChange(event, "totalInventoryCount")}
              onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
            />
            <SearchIcon
              sx={{
                color: "#6F6F6F",
                position: "absolute",
                top: "50%",
                left: "8px",
                transform: "translateY(-50%)",
              }}
            />
          </div>
        </div>
      ),
      renderCell: (params: any) => (
        <CellWithTooltip value={params.value || "-"} />
      ),
    },
    {
      field: "contactPerson",
      headerName: "Contact Person",
      flex: 1,
      minWidth: 200,
      sortable: true,
      valueGetter: (value: any, row: any) => `${row.contactPerson ?? ""}`,
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: (params) => (
        <div>
          <Typography
            variant="body2"
            sx={{ color: "#6F6F6F", fontSize: "14px" }}
          >
            {params.colDef.headerName}
          </Typography>
          <div className="position-relative">
            <input
              type="text"
              className="form-control border mt-1"
              style={{ textIndent: "24px", boxShadow: "none" }}
              placeholder="Search"
              // value={filter.contactPerson}
              defaultValue={filter.contactPerson}
              onChange={(event) => handleFilterChange(event, "contactPerson")}
              onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
            />
            <SearchIcon
              sx={{
                color: "#6F6F6F",
                position: "absolute",
                top: "50%",
                left: "8px",
                transform: "translateY(-50%)",
              }}
            />
          </div>
        </div>
      ),
      renderCell: (params: any) => (
        <CellWithTooltip value={params.value || "-"} />
      ),
    },
    {
      field: "contactNumber",
      headerName: "Contact Number",
      flex: 1,
      minWidth: 200,
      sortable: true,
      valueGetter: (value: any, row: any) => `${row.contactNumber ?? ""}`,
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: (params) => (
        <div>
          <Typography
            variant="body2"
            sx={{ color: "#6F6F6F", fontSize: "14px" }}
          >
            {params.colDef.headerName}
          </Typography>
          <div className="position-relative">
            <input
              type="text"
              className="form-control border mt-1"
              style={{ textIndent: "24px", boxShadow: "none" }}
              placeholder="Search"
              // value={filter.contactNumber}
              defaultValue={filter.contactNumber}
              onChange={(event) => handleFilterChange(event, "contactNumber")}
              onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
            />
            <SearchIcon
              sx={{
                color: "#6F6F6F",
                position: "absolute",
                top: "50%",
                left: "8px",
                transform: "translateY(-50%)",
              }}
            />
          </div>
        </div>
      ),
      renderCell: (params: any) => (
        <CellWithTooltip value={params.value || "-"} />
      ),
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
      minWidth: 200,
      sortable: true,
      valueGetter: (value: any, row: any) => `${row.email ?? ""}`,
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: (params) => (
        <div>
          <Typography
            variant="body2"
            sx={{ color: "#6F6F6F", fontSize: "14px" }}
          >
            {params.colDef.headerName}
          </Typography>
          <div className="position-relative">
            <input
              type="text"
              className="form-control border mt-1"
              style={{ textIndent: "24px", boxShadow: "none" }}
              placeholder="Search"
              value={filter.email}
              // defaultValue={filter.email}
              onChange={(event) => handleFilterChange(event, "email")}
              onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
            />
            <SearchIcon
              sx={{
                color: "#6F6F6F",
                position: "absolute",
                top: "50%",
                left: "8px",
                transform: "translateY(-50%)",
              }}
            />
          </div>
        </div>
      ),
      renderCell: (params: any) => (
        <CellWithTooltip value={params.value || "-"} />
      ),
    },
    {
      field: "action",
      headerName: "Action",
      sortable: false,
      minWidth: 120,
      flex: 1,
      headerClassName: "table-header border-0 my-2 h-100",
      renderCell: (params: any) => (
        <>
          {CommonService.isAdmin() && (
            <div
              className="d-flex align-items-center gap-2 h-100"
              onClick={(e) => e.stopPropagation()}
            >
              <Tooltip title="Edit" arrow>
                <IconButton
                  aria-label="message"
                  size="small"
                  onClick={() => handleEditCompany(params?.row?.id)}
                  disabled={
                    params?.row?.roleName == "SUPER_ADMIN" &&
                    CommonService.isUser()
                  }
                >
                  <img
                    src={EditIcon}
                    className="cursor-pointer"
                    alt="EditIcon"
                  />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete" arrow>
                <IconButton
                  aria-label="message"
                  size="small"
                  onClick={() => handleDeleteCompany(params?.row?.id)}
                  disabled={
                    params?.row?.roleName == "SUPER_ADMIN" &&
                    CommonService.isUser()
                  }
                >
                  <img
                    src={DeleteIcon}
                    className="cursor-pointer"
                    alt="DeleteIcon"
                  />
                </IconButton>
              </Tooltip>
            </div>
          )}
        </>
      ),
    },
  ];

  // Map filtered rows with unique IDs
  const tableDataWithIds = companiesList.map((rowData, index) => ({
    ...rowData,
  }));

  const handleBack = () => {
    history.push("/master/dashboard");
  };

  const reloadTable = () => {

    //fetch all the companies based of active tab
    if (isActiveCompanyClicked.current) {
      showActiveCompanies(page, pageSize, filter, sortField, sortDirection, false);
      fetchAllSuspendedCompaniesCount();
      fetchAllCompaniesCount();
      fetchAllActiveCompaniesCount();
    } else if (isSuspendedCompanyClicked.current) {
      showSuspendedCompanies(page, pageSize, filter, sortField, sortDirection, false);
      fetchAllActiveCompaniesCount();
      fetchAllCompaniesCount();
      fetchAllSuspendedCompaniesCount();
    } else {
      showAllCompanies(page, pageSize, filter, sortField, sortDirection, false);
      fetchAllActiveCompaniesCount();
      fetchAllSuspendedCompaniesCount();
      fetchAllCompaniesCount();
    }
  };

  const handleSelectionModelChange = (newSelection: GridRowId[]) => {
    setSelectionModel(newSelection);
    const newSelectedRows = newSelection.map((id: GridRowId) => {
      return companiesList.find((row: any) => row.id === id); // Assuming each row object has an 'id' property
    });
  };

  return (
    <>
    <div className="usersList companyList" >
      <div className="usersList__header--wrapper d-flex flex-wrap justify-content-between align-items-center position-relative gap-2">
        <div className="d-flex align-items-center flex-wrap gap-2">
          <IconButton
            onClick={handleBack}
            aria-label="ArrowBackIcon"
            sx={{
              backgroundColor: "#FFF",
              mb: 1,
              "&:hover": { backgroundColor: "#FFF" },
            }}
          >
            <ArrowBackIcon sx={{ color: "#1C1B1F" }} />
          </IconButton>
          <div className="header--title mb-2">
            <h1 className="title mb-0">
              Account Owners <i className="bi bi-heart-fill"></i>
            </h1>
          </div>
          <div className="menu-items__btnGroup btnGroup-border divider d-inline-flex mx-2"></div>
          <div className="menu-items__btnGroup d-inline-flex">
              <button
                className="primary-btn d-inline-flex me-1"
                onClick={handleAddCompany}
              >
                <AddSharpIcon sx={{ mr: 0.5 }} />
                Add Account Owner
              </button>
            </div>
            {/* <div className="divider mx-2"></div> */}
            {companiesList.length > 0 || isLoading ?
              <>
                <button title="Export to Excel"
                  className="primary-btn d-inline-flex align-items-center me-1"
                  style={{ paddingTop: "4px", paddingLeft: 11 }}
                  onClick={(e) => {
                    e.currentTarget.blur(); // Removes focus after clicking
                    exportCompanies(page, pageSize, filter, sortField, sortDirection);
                  }}
                >
                  <FileDownloadOutlinedIcon sx={{ marginTop: "5.5px" }} />
                  Export
                </button>
              </>
              : null}
        </div>
          <div className={`header--menu ms-auto ${(selectionModel.length > 0 && isSuspendedCompanyClicked.current) || 
        (selectionModel.length > 0 &&isActiveCompanyClicked.current) ? 'dynamicBtn':''}`}>
            <div className="header--menu-items d-flex flex-wrap justify-content-end gap-2">
              {/* {selectionModel.length > 0 && (
                <div className="col-auto mb-2">
                  <DeleteCompany
                    deleteCompany={selectionModel}
                    reloadTable={reloadTable}
                  />
                </div>
              )} */}

              {/* {selectionModel.length > 0 && isSuspendedCompanyClicked.current && (
                <div className="col-auto">
                  <ActiveCompany
                    activeCompany={selectionModel}
                    reloadTable={reloadTable}
                  />
                </div>
              )} */}

              {/* {selectionModel.length > 0 &&
                (isActiveCompanyClicked.current) && (
                  <>
                  <div className="col-auto">
                    <SuspendCompany
                      suspendCompany={selectionModel}
                      reloadTable={reloadTable}
                    />
                  </div>
                  <div className="menu-items__btnGroup btnGroup-border d-inline-flex mb-2 mx-2"></div>
                  </>
                )} */}


              {/* <div className="menu-items__btnGroup d-inline-flex">
                <button
                  className="primary-btn d-inline-flex me-1"
                  onClick={handleAddCompany}
                >
                  <AddSharpIcon sx={{ mr: 0.5 }} />
                  Add Account Owner
                </button>
              </div> */}
              <div
                className="btn-group user-activity-btn-grp flex-wrap"
                role="group"
                aria-label="Basic mixed styles example"
              >
                <button
                  className={`tab-btn d-inline-flex justify-content-between align-items-center ${
                    isActiveCompanyClicked.current ? "bg-theme" : "bg-theme-white"
                  }`}
                  onClick={() => {
                    showActiveCompanies(
                      page,
                      pageSize,
                      filter,
                      sortField,
                      sortDirection,
                      true
                    );
                    fetchAllActiveCompaniesCount();
                  }}
                >
                Active
                  <span
                    className={`review--number d-inline-flex align-items-center justify-content-center rounded-circle ms-2 ${
                      isActiveCompanyClicked.current
                        ? "bg-theme-white"
                        : "bg-theme"
                    }`}
                  >
                    {activeCompanyCount}
                  </span>
                </button>

                <button
                  className={`tab-btn d-inline-flex justify-content-between align-items-center ${
                    isSuspendedCompanyClicked.current ? "bg-theme" : "bg-theme-white"
                  }`}
                  onClick={() => {
                    showSuspendedCompanies(
                      page,
                      pageSize,
                      filter,
                      sortField,
                      sortDirection,
                      true
                    );
                    fetchAllSuspendedCompaniesCount();
                  }}
                >
                Suspended
                  <span
                    className={`review--number d-inline-flex align-items-center justify-content-center rounded-circle ms-2 ${
                      isSuspendedCompanyClicked.current
                        ? "bg-theme-white"
                        : "bg-theme"
                    }`}
                  >
                    {suspendedCompanyCount}
                  </span>
                </button>

                <button
                  className={`tab-btn d-inline-flex justify-content-between align-items-center ${
                    isAllCompanyClicked.current ? "bg-theme" : "bg-theme-white"
                  }`}
                  onClick={() => {
                    showAllCompanies(
                      page,
                      pageSize,
                      filter,
                      sortField,
                      sortDirection,
                      true
                    );
                    fetchAllCompaniesCount();
                  }}
                >
                  All
                  <span
                    className={`review--number d-inline-flex align-items-center justify-content-center rounded-circle ms-2 ${
                      isAllCompanyClicked.current
                        ? "bg-theme-white"
                        : "bg-theme"
                    }`}
                  >
                    {allCompanyCount}
                  </span>
                </button>
              </div>
{/*             
            <div>
            {selectionModel.length > 0 && isSuspendedCompanyClicked.current && (
                <div className="col-auto ms-2">
                  <ActiveCompany
                    activeCompany={selectionModel}
                    reloadTable={reloadTable}
                  />
                </div>
              )}

              {selectionModel.length > 0 &&
                (isActiveCompanyClicked.current) && (
                  <>
                  <div className="col-auto ms-2">
                    <SuspendCompany
                      suspendCompany={selectionModel}
                      reloadTable={reloadTable}
                    />
                  </div>
                  <div className="menu-items__btnGroup btnGroup-border d-inline-flex mb-2 mx-2"></div>
                  </>
                )}
          </div> */}
          </div>
          </div>
          {/* <div>
            {selectionModel.length > 0 && isSuspendedCompanyClicked.current && (
                <div className="col-auto">
                  <ActiveCompany
                    activeCompany={selectionModel}
                    reloadTable={reloadTable}
                  />
                </div>
              )}

              {selectionModel.length > 0 &&
                (isActiveCompanyClicked.current) && (
                  <>
                  <div className="col-auto">
                    <SuspendCompany
                      suspendCompany={selectionModel}
                      reloadTable={reloadTable}
                    />
                  </div>
                  <div className="menu-items__btnGroup btnGroup-border d-inline-flex mb-2 mx-2"></div>
                  </>
                )}
          </div> */}
          <div className={`blank-div text-end ${(selectionModel.length > 0 && isSuspendedCompanyClicked.current) || 
        (selectionModel.length > 0 &&isActiveCompanyClicked.current) ? 'dynamicBtn':''}`}>
            {selectionModel.length > 0 && isSuspendedCompanyClicked.current && (
                <div className="col-auto">
                  <ActiveCompany
                    activeCompany={selectionModel}
                    reloadTable={reloadTable}
                  />
                </div>
              )}

              {selectionModel.length > 0 &&
                (isActiveCompanyClicked.current) && (
                  <>
                  <div className="col-auto">
                    <SuspendCompany
                      suspendCompany={selectionModel}
                      reloadTable={reloadTable}
                    />
                  </div>
                  </>
                )}
          </div>
      </div>

      <div className="user-table-container">
        <div className="userTable">
          <DataGrid
            rows={tableDataWithIds}
            columns={columns}
            className="data-grid"
            initialState={{
              pagination: {
                paginationModel: { page, pageSize },
              },
            }}
            pagination
            paginationModel={{ page, pageSize }}
            onPaginationModelChange={onPaginationModelChange}
            rowCount={totalCount}
            paginationMode="server"
            sortingMode="server"
            checkboxSelection
            onSortModelChange={handleSortChange}
            disableRowSelectionOnClick={true}
            pageSizeOptions={[10, 20, 50, 100]}
            localeText={localeText}
            onRowClick={handleRowClick}
            onRowSelectionModelChange={handleSelectionModelChange}
            loading={isLoading}
            slots={{
              loadingOverlay: DataGridLoader,
            }}
            disableColumnResize
            sx={{
              "& .MuiDataGrid-columnHeaderCheckbox": {
                borderBottom: "unset !important"
              },
            }}
          />
        </div>
      </div>
    </div>
      <Modal
        show={deleteCompany}
        onHide={handleClose}
        className="modal__wd modal__wd--submitSellReq barrel-assignment-modal signup-success-modal"
        size="lg"
        centered
      >
        <Modal.Header className="align-items-center justify-content-between">
          <Modal.Title className="modal-title">  Account Owner Delete Confirmation</Modal.Title>
          <button onClick={handleClose} type="button" className="p-0 bg-transparent border-0 outline-0" data-bs-dismiss="modal" aria-label="Close">
            <CloseIcon sx={{ color: "#ffffff" }} />
          </button>
        </Modal.Header>

        <Modal.Body>
          <div className="form__inner scanned-popup-wrapper">
            {/* <div className="text-center d-flex justify-content-center">
                <span className="warning-icon-wrapper">
                </span>
              </div> */}
            <div className="text-center">
              <span className="warning-icon-wrapper mx-auto">
                <img src={ScannedWarning} alt="Product Checkout icon" />
              </span>
            </div>

            <h2 className="modal-body__title text-center">
              {!isCompanyDeletable ? "Are you sure you want to delete this account owner?" : "You can’t delete this account owner"}
            </h2>

            <p className="modal-text">
              {!isCompanyDeletable 
                ? "This account owner is not connected to any active service requests or containers. You can safely delete the account owner, and it will no longer appear in the active account owner list."             
                : "This account owner is currently linked to active service requests or containers. Please remove or complete those connections before deleting the account owner."
              }
            </p>
          </div>
        </Modal.Body>

        <Modal.Footer className="justify-content-center">
          <div className="btn-group d-flex justify-content-center flex-wrap w-100 gap-3">
            {!isCompanyDeletable ? (
              <>
                <button className="secondary-btn" onClick={handleClose} disabled={isLoading} style={{ minWidth: "190px" }}>
                  No
                </button>
                <button
                  className="primary-btn"
                  disabled={isLoading}
                  onClick={confirmDeleteCompany}
                  style={{ minWidth: "190px" }}
                >
                  {isLoading && <Spinner />} Yes
                </button>
              </>
            ) : (
                <button className="primary-btn" onClick={handleClose} style={{ minWidth: "190px" }}>
                  {isLoading && <Spinner />} Ok
                </button>
            )}
          </div>
        </Modal.Footer>
      </Modal>
        </>
  );
};

export default CompanyList;
